import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchRewards,
  fetchRedemptions,
  fetchPointsLedger,
  createRedemption,
} from '../lib/api';

export function useRewards() {
  return useQuery({
    queryKey: ['rewards'],
    queryFn: fetchRewards,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRedemptions(status?: 'pending' | 'processing' | 'fulfilled' | 'failed') {
  return useQuery({
    queryKey: ['redemptions', status],
    queryFn: () => fetchRedemptions(status),
    staleTime: 0,
  });
}

export function usePendingRedemptions() {
  return useQuery({
    queryKey: ['redemptions', 'pending-processing'],
    queryFn: async () => {
      const [pending, processing] = await Promise.all([
        fetchRedemptions('pending'),
        fetchRedemptions('processing'),
      ]);
      return [...pending, ...processing];
    },
    refetchInterval: 10_000, // poll every 10s per spec
    staleTime: 0,
  });
}

export function usePointsLedger(page = 1) {
  return useQuery({
    queryKey: ['points-ledger', page],
    queryFn: () => fetchPointsLedger(page),
  });
}

export function useCreateRedemption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rewardId: string) => createRedemption(rewardId),
    onSuccess: () => {
      // Invalidate balance, ledger, and all redemption queries after a new redemption.
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['redemptions'] });
      void queryClient.invalidateQueries({ queryKey: ['points-ledger'] });
    },
  });
}
